import { z } from 'zod';
import { http } from './http';
import { env } from '@/lib/env';

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ /access/me — a VERDADE sobre o que este utilizador pode fazer.            ║
// ║                                                                          ║
// ║ Antes: o frontend recebia uma string (`user_role`) e ADIVINHAVA o papel   ║
// ║ com um enum de 3 valores. Agora recebe as permissões efetivas, resolvidas ║
// ║ na BD pelo motor de política. A UI deixa de ter RBAC próprio (e deixa de  ║
// ║ poder discordar do backend — que era como nasciam os ecrãs "fantasma").   ║
// ╚══════════════════════════════════════════════════════════════════════════╝

const accessSchema = z.object({
  authenticated: z.boolean(),
  isStaff: z.boolean().default(false),        // equipa Marcianus (HQ) — eixo A
  isOrganizer: z.boolean().default(false),    // membro de ≥1 org — eixo B
  activeOrgId: z.string().nullish(),          // a org a que estas permissões dizem respeito
  impersonatedBy: z.string().nullish(),
  permissions: z.array(z.string()).default([]),
  user: z.object({
    userId: z.string(),
    platformRole: z.string().nullish(),
    orgs: z.array(z.object({ orgId: z.string(), role: z.string() })).default([]),
  }).nullish(),
});
export type Access = z.infer<typeof accessSchema>;

export const ANONYMOUS: Access = {
  authenticated: false, isStaff: false, isOrganizer: false, permissions: [], user: null,
};

/** Permissões do harness de QA/preview (sem backend) — espelham a matriz da BD. */
const ORG_PERMISSIONS = [
  'org.view', 'org.events.view', 'org.events.manage', 'org.sales.view', 'org.sales.manage',
  'org.checkin.operate', 'org.analytics.view', 'org.team.view', 'org.team.manage',
  'org.branding.manage', 'org.billing.view', 'org.billing.manage',
];

const QA_PERMISSIONS: Record<string, string[]> = {
  participant: [],
  organizer: ORG_PERMISSIONS,
  // Em produção, um staff NÃO tem permissões de org: para entrar na consola de um
  // cliente atravessa o tenant (platform.tenant.override) ou impersona — e o
  // servidor resolve isso com o escopo do pedido. O harness não tem servidor com
  // quem negociar escopo, e o seu trabalho é conseguir abrir TODOS os ecrãs para
  // QA visual — por isso, e só aqui, o admin vem com as duas listas.
  admin: [
    'platform.access', 'platform.metrics.view', 'platform.orgs.view', 'platform.users.view',
    'platform.cms.manage', 'platform.hexis.manage', 'platform.audit.view', 'platform.tenant.override',
    ...ORG_PERMISSIONS,
  ],
};

export async function getAccess(): Promise<Access> {
  if (env.preview || env.mocksEnabled) {
    const qaRole = localStorage.getItem('marc:qa:role') || 'participant';
    return accessSchema.parse({
      authenticated: true,
      isStaff: qaRole === 'admin',
      isOrganizer: qaRole === 'organizer',
      permissions: QA_PERMISSIONS[qaRole] ?? [],
      user: { userId: 'usr_mock_123', platformRole: qaRole === 'admin' ? 'platform_admin' : null, orgs: [] },
    });
  }
  // Falhar fechado: sem resposta do servidor, o utilizador não tem permissões.
  return http.get<unknown>('/access/me')
    .then((raw) => accessSchema.parse(raw))
    .catch(() => ANONYMOUS);
}
